import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
    X, Plus, Trash2, Save, Globe, Building2, DollarSign,
    CreditCard, AlertCircle, CheckCircle, Loader2, Clock
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import CountryDropdown from "../../components/ui/CountryDropdown";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
    createSubscriptionPlan,
    updateSubscriptionPlan,
    bulkCreateSubscriptionPlanAreas,
    bulkUpdateSubscriptionPlanAreas,
    deleteSubscriptionPlanArea,
    clearSubscriptionPlansError,
    clearSubscriptionPlanAreasError
} from "../../redux/Slices/subscriptionPlanSlice";
import { getUserCategories } from "../../redux/Actions/AdminAction";
import { SubscriptionPlanTypeEnum, SubscriptionPlanAvailableTimeEnum, SubscriptionPlanAreaAvailableTimeEnum, SubscriptionPlanAvailableTimeRef, SubscriptionPlanAreaAvailableTimeRef } from "../../modules/shared/model";

interface SubscriptionPlanModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    editPlan?: any;
}

interface PlanSection {
    id: string;
    country: { id: string; name: string; code: string } | null;
    category: { id: string; name: string } | null;
    credits: number;
    amount: number;
    discount: number;
    availableTime?: SubscriptionPlanAreaAvailableTimeEnum;
}

const SubscriptionPlanModal: React.FC<SubscriptionPlanModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    editPlan
}) => {
    const dispatch = useAppDispatch();

    // Redux state
    const { plansLoading, plansError, areasLoading, areasError } = useAppSelector(
        (state) => state.subscriptionPlans
    );
    const { categories } = useAppSelector((state) => state.admin);
    console.log(categories, "categories");
    // Form state
    const [formData, setFormData] = useState({
        name: "",
        amount: null,
        credits: null,
        discount: null,
        type: SubscriptionPlanTypeEnum.Regular,
        availableTime: null
    });

    const [planSections, setPlanSections] = useState<PlanSection[]>([
        {
            id: "1",
            country: null,
            category: null,
            credits: null,
            amount: null,
            discount: null,
            availableTime: null
        }
    ]);

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [existingAreas, setExistingAreas] = useState<any[]>([]);
    const [deletingAreas, setDeletingAreas] = useState(false);

    // Load initial data
    useEffect(() => {
        if (isOpen) {
            // Load categories
            if (categories.data.length === 0) {
                dispatch(getUserCategories({ page: 1, limit: 10, isShowLoader: false }));
            }
        }
    }, [isOpen, dispatch, categories.data.length]);

    // Clear errors when modal opens
    useEffect(() => {
        if (isOpen) {
            dispatch(clearSubscriptionPlansError());
            dispatch(clearSubscriptionPlanAreasError());
            setErrors({});
        }
    }, [isOpen, dispatch]);

    // Load existing plan data when editing
    useEffect(() => {
        if (isOpen && editPlan) {
            console.log("Edit plan data:", editPlan);
            console.log("Plan type:", editPlan.type);
            console.log("Plan type id:", editPlan.type?.id);
            console.log("Plan availableTime:", editPlan.availableTime);
            if (editPlan.areas) {
                console.log("Plan areas:", editPlan.areas);
                editPlan.areas.forEach((area, index) => {
                    console.log(`Area ${index} availableTime:`, area.availableTime);
                });
            }
            const formDataToSet = {
                name: editPlan.name || "",
                amount: editPlan.amount || null,
                credits: editPlan.credits || null,
                discount: editPlan.discount || null,
                type: Number(editPlan.type?.id) || SubscriptionPlanTypeEnum.Regular,
                availableTime: Number(editPlan.availableTime?.id) || null
            };
            console.log("Setting formData:", formDataToSet);
            console.log("FormData type value:", formDataToSet.type);
            console.log("FormData availableTime value:", formDataToSet.availableTime);
            console.log("Is Emergency?", formDataToSet.type === SubscriptionPlanTypeEnum.Emergency);
            setFormData(formDataToSet);

            // Use areas from the plan data
            if (editPlan.areas && editPlan.areas.length > 0) {
                setExistingAreas(editPlan.areas);
                
                // Convert areas to plan sections format
                const sections = editPlan.areas.map((area, index) => ({
                    id: area.id || `existing_${index}`,
                    country: area.country,
                    category: area.category,
                    credits: area.credits,
                    amount: area.amount,
                    discount: area.discount,
                    availableTime: Number(area.availableTime?.id) || null
                }));
                
                console.log("Setting plan sections:", sections);
                sections.forEach((section, index) => {
                    console.log(`Section ${index} availableTime:`, section.availableTime);
                });
                setPlanSections(sections);
            }
        } else if (isOpen && !editPlan) {
            // Reset form for new plan
            setFormData({
                name: "",
                amount: null,
                credits: null,
                discount: null,
                type: SubscriptionPlanTypeEnum.Regular,
                availableTime: null as SubscriptionPlanAvailableTimeRef | null
            });
            setPlanSections([{
                id: "1",
                country: null,
                category: null,
                credits: null,
                amount: null,
                discount: null,
                availableTime: null
            }]);
            setExistingAreas([]);
            setDeletingAreas(false);
        }
    }, [isOpen, editPlan, dispatch]);

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = "Plan name is required";
        }

        if (formData.amount <= 0) {
            newErrors.amount = "Amount must be greater than 0";
        }

        if (formData.credits <= 0) {
            newErrors.credits = "Credits must be greater than 0";
        }

        if (formData.discount < 0 || formData.discount > 100) {
            newErrors.discount = "Discount must be between 0 and 100";
        }

        if (formData.type === SubscriptionPlanTypeEnum.Emergency && !formData.availableTime) {
            newErrors.availableTime = "Available time is required for emergency plans";
        }

        // Validate sections - only validate if country is provided
        planSections.forEach((section, index) => {
            // Country is required for any area-specific pricing
            if (section.category && !section.country) {
                newErrors[`section_${index}_country`] = "Country is required when category is selected";
            }
            
            // If country is selected, validate credits and amount
            if (section.country) {
                if (section.credits <= 0) {
                    newErrors[`section_${index}_credits`] = "Credits must be greater than 0";
                }
                if (section.amount <= 0) {
                    newErrors[`section_${index}_amount`] = "Amount must be greater than 0";
                }
                if (section.discount < 0 || section.discount > 100) {
                    newErrors[`section_${index}_discount`] = "Discount must be between 0 and 100";
                }
                
                // Validate availableTime for emergency plans
                if (formData.type === SubscriptionPlanTypeEnum.Emergency && !section.availableTime) {
                    newErrors[`section_${index}_availableTime`] = "Available time is required for emergency plan areas";
                }
            }
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            const planData = {
                name: formData.name,
                amount: formData.amount,
                credits: formData.credits,
                discount: formData.discount,
                type: {
                    id: Number(formData.type),
                    name: SubscriptionPlanTypeEnum[formData.type]
                },
                availableTime: formData.availableTime ? { id: Number(formData.availableTime) } : undefined
            };
            
            console.log("Plan data being submitted:", planData);
            console.log("AvailableTime value:", formData.availableTime);
            console.log("AvailableTime as number:", Number(formData.availableTime));

            let planId: string;

            if (editPlan) {
                // Update existing plan
                const updatedPlan = await dispatch(updateSubscriptionPlan({ 
                    id: editPlan.id, 
                    data: planData 
                })).unwrap();
                planId = updatedPlan.id;

                // Identify areas to delete (existing areas not in current form)
                const currentAreaIds = planSections
                    .filter(section => section.id.startsWith("existing_") || existingAreas.some(area => area.id === section.id))
                    .map(section => section.id.startsWith("existing_") 
                        ? existingAreas[parseInt(section.id.split("_")[1])]?.id 
                        : section.id
                    )
                    .filter(Boolean);

                const areasToDelete = existingAreas
                    .filter(area => !currentAreaIds.includes(area.id))
                    .map(area => area.id);

                // Delete removed areas
                if (areasToDelete.length > 0) {
                    console.log("Deleting areas:", areasToDelete);
                    setDeletingAreas(true);
                    try {
                        // Delete all areas in parallel for better performance
                        await Promise.all(
                            areasToDelete.map(areaId => 
                                dispatch(deleteSubscriptionPlanArea(areaId)).unwrap()
                            )
                        );
                        console.log(`Successfully deleted ${areasToDelete.length} area(s)`);
                        // Show success message
                        if (areasToDelete.length === 1) {
                            console.log("1 area was removed from the subscription plan.");
                        } else {
                            console.log(`${areasToDelete.length} areas were removed from the subscription plan.`);
                        }
                    } catch (error) {
                        console.error("Failed to delete some areas:", error);
                        // Continue with the rest of the operation even if some deletions fail
                    } finally {
                        setDeletingAreas(false);
                    }
                }

                // Handle areas update using bulk update API
                const updates = planSections
                    .filter(section => section.id.startsWith("existing_") || existingAreas.some(area => area.id === section.id))
                    .map(section => ({
                        id: section.id.startsWith("existing_") 
                            ? existingAreas[parseInt(section.id.split("_")[1])]?.id 
                            : section.id,
                        data: {
                            category: section.category ? { 
                                id: section.category.id,
                                name: section.category.name
                            } : undefined,
                            country: { 
                                id: section.country!.id,
                                name: section.country!.name,
                                code: section.country!.code || ""
                            },
                            subscriptionPlan: { 
                                id: planId,
                                name: formData.name,
                                amount: formData.amount,
                                discount: formData.discount
                            },
                            credits: section.credits,
                            amount: section.amount,
                            discount: section.discount,
                            availableTime: section.availableTime ? { id: Number(section.availableTime) } : undefined
                        }
                    }))
                    .filter(update => update.id); // Only include valid updates

                if (updates.length > 0) {
                    await dispatch(bulkUpdateSubscriptionPlanAreas(updates)).unwrap();
                }

                // Handle new areas (those without existing IDs)
                const newAreas = planSections.filter(section => 
                    !section.id.startsWith("existing_") && 
                    !existingAreas.some(area => area.id === section.id)
                );

                if (newAreas.length > 0) {
                    const newAreasData = newAreas.map(section => ({
                        category: section.category ? {
                            id: section.category.id,
                            name: section.category.name
                        } : undefined,
                        country: {
                            id: section.country!.id,
                            name: section.country!.name,
                            code: section.country!.code || ""
                        },
                        subscriptionPlan: {
                            id: planId,
                            name: formData.name,
                            amount: formData.amount,
                            discount: formData.discount
                        },
                        credits: section.credits,
                        amount: section.amount,
                        discount: section.discount,
                        availableTime: section.availableTime ? { id: Number(section.availableTime) } : undefined
                    }));
                    
                    console.log("New areas data being submitted:", newAreasData);

                    await dispatch(bulkCreateSubscriptionPlanAreas({ areas: newAreasData })).unwrap();
                }
            } else {
                // Create new plan
                const createdPlan = await dispatch(createSubscriptionPlan(planData)).unwrap();
                planId = createdPlan.id;

                // Create plan areas only if country is selected
                const validSections = planSections.filter(section => 
                    section.country && section.credits && section.amount
                );

                if (validSections.length > 0) {
                    const areasData = validSections.map(section => ({
                        category: section.category ? {
                            id: section.category.id,
                            name: section.category.name
                        } : undefined,
                        country: {
                            id: section.country!.id,
                            name: section.country!.name,
                            code: section.country!.code || ""
                        },
                        subscriptionPlan: {
                            id: planId,
                            name: formData.name,
                            amount: formData.amount,
                            discount: formData.discount
                        },
                        credits: section.credits,
                        amount: section.amount,
                        discount: section.discount,
                        availableTime: section.availableTime ? { id: Number(section.availableTime) } : undefined
                    }));
                    
                    console.log("Areas data being submitted:", areasData);

                    await dispatch(bulkCreateSubscriptionPlanAreas({ areas: areasData })).unwrap();
                }
            }

            // Success
            onSuccess?.();
            onClose();

            // Reset form
            setFormData({
                name: "",
                amount: null,
                credits: null,
                discount: null,
                type: SubscriptionPlanTypeEnum.Regular,
                availableTime: null as SubscriptionPlanAvailableTimeRef | null
            });
            setPlanSections([{
                id: "1",
                country: null,
                category: null,
                credits: null,
                amount: null,
                discount: null,
                availableTime: null
            }]);
            setExistingAreas([]);
            setDeletingAreas(false);

        } catch (error) {
            console.error("Error saving subscription plan:", error);
        }
    };

    const addSection = () => {
        const newSection: PlanSection = {
            id: Date.now().toString(),
            country: null,
            category: null,
            credits: null,
            amount: null,
            discount: null,
            availableTime: null
        };
        setPlanSections([...planSections, newSection]);
    };

    const removeSection = (sectionId: string) => {
        if (planSections.length > 1) {
            setPlanSections(planSections.filter(section => section.id !== sectionId));
        }
    };

    const updateSection = (sectionId: string, field: keyof PlanSection, value: any) => {
        setPlanSections(planSections.map(section =>
            section.id === sectionId ? { ...section, [field]: value } : section
        ));
    };

    const handleCountrySelect = (sectionId: string, country: any) => {
        updateSection(sectionId, "country", country);
    };

    const handleCategorySelect = (sectionId: string, categoryId: string) => {
        const category = categories.data.find(c => c.id === categoryId);
        updateSection(sectionId, "category", category || null);
    };

    // Helper function to get areas that will be deleted
    const getAreasToDelete = () => {
        if (!editPlan || !existingAreas.length) return [];
        
        const currentAreaIds = planSections
            .filter(section => section.id.startsWith("existing_") || existingAreas.some(area => area.id === section.id))
            .map(section => section.id.startsWith("existing_") 
                ? existingAreas[parseInt(section.id.split("_")[1])]?.id 
                : section.id
            )
            .filter(Boolean);

        return existingAreas.filter(area => !currentAreaIds.includes(area.id));
    };

    if (!isOpen) return null;

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
            <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.95 }}
                className="bg-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto"
            >
                <div className="p-6 border-b sticky top-0 bg-white">
                     <div className="flex items-center justify-between">
                         <h2 className="text-xl font-semibold">
                             {editPlan ? "Edit Subscription Plan" : "Create New Subscription Plan"}
                         </h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full"
                        >
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Basic Plan Information */}
                    <Card className="p-6">
                        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
                            <CreditCard className="h-5 w-5" />
                            Plan Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plan Name *
                                </label>
                                <Input
                                    type="text"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    placeholder="Enter plan name"
                                    className={errors.name ? "border-red-500" : ""}
                                />
                                {errors.name && (
                                    <p className="text-red-500 text-sm mt-1">{errors.name}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Plan Type *
                                </label>
                                <select
                                    value={formData.type}
                                    onChange={(e) => setFormData({ ...formData, type: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value={SubscriptionPlanTypeEnum.Regular}>Regular Service</option>
                                    <option value={SubscriptionPlanTypeEnum.Emergency}>Emergency Service</option>
                                </select>
                            </div>

                            {formData.type === SubscriptionPlanTypeEnum.Emergency && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Available Time *
                                    </label>
                                    <select
                                        value={formData.availableTime || ""}
                                        onChange={(e) => setFormData({ ...formData, availableTime: e.target.value ? Number(e.target.value) : null })}
                                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.availableTime ? "border-red-500" : "border-gray-300"}`}
                                    >
                                        <option value="">Select available time</option>
                                        <option value={SubscriptionPlanAvailableTimeEnum.Day}>Day (6 AM - 6 PM)</option>
                                        <option value={SubscriptionPlanAvailableTimeEnum.Night}>Night (6 PM - 6 AM)</option>
                                        <option value={SubscriptionPlanAvailableTimeEnum.FullDay}>Full Day (24 Hours)</option>
                                    </select>
                                    {errors.availableTime && (
                                        <p className="text-red-500 text-sm mt-1">{errors.availableTime}</p>
                                    )}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Base Amount *
                                </label>
                                <Input
                                    type="number"
                                    step="0.01"
                                    value={formData.amount}
                                    onChange={(e) => setFormData({ ...formData, amount: Number(e.target.value) })}
                                    placeholder="0.00"
                                    className={errors.amount ? "border-red-500" : ""}
                                />
                                {errors.amount && (
                                    <p className="text-red-500 text-sm mt-1">{errors.amount}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Base Credits *
                                </label>
                                <Input
                                    type="number"
                                    value={formData.credits}
                                    onChange={(e) => setFormData({ ...formData, credits: Number(e.target.value) })}
                                    placeholder="0"
                                    className={errors.credits ? "border-red-500" : ""}
                                />
                                {errors.credits && (
                                    <p className="text-red-500 text-sm mt-1">{errors.credits}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Base Discount (%)
                                </label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={formData.discount}
                                    onChange={(e) => setFormData({ ...formData, discount: Number(e.target.value) })}
                                    placeholder="0"
                                    className={errors.discount ? "border-red-500" : ""}
                                />
                                {errors.discount && (
                                    <p className="text-red-500 text-sm mt-1">{errors.discount}</p>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Plan Sections */}
                    <Card className="p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-medium flex items-center gap-2">
                                <Globe className="h-5 w-5" />
                                Plan Areas & Pricing
                            </h3>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={addSection}
                                className="flex items-center gap-2"
                            >
                                <Plus className="h-4 w-4" />
                                Add Section
                            </Button>
                        </div>

                        {/* Areas to be deleted warning */}
                        {editPlan && getAreasToDelete().length > 0 && (
                            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                <div className="flex items-center gap-2 text-yellow-700">
                                    <AlertCircle className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                        {getAreasToDelete().length === 1 
                                            ? "1 area will be removed" 
                                            : `${getAreasToDelete().length} areas will be removed`
                                        } when you save this plan.
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="space-y-4">
                            {planSections.map((section, index) => (
                                <div key={section.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h4 className="font-medium">Section {index + 1}</h4>
                                        {planSections.length > 1 && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => removeSection(section.id)}
                                                className="text-red-600 hover:text-red-700"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                        {/* Country Selection */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Country *
                                            </label>
                                            <CountryDropdown
                                                selectedCountry={section.country}
                                                onCountrySelect={(country) => handleCountrySelect(section.id, country)}
                                                placeholder="Select a country..."
                                                placement="top"
                                                className={errors[`section_${index}_country`] ? "border-red-500" : ""}
                                            />
                                            {errors[`section_${index}_country`] && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {errors[`section_${index}_country`]}
                                                </p>
                                            )}
                                        </div>

                                        {/* Category Selection */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Category
                                            </label>
                                            <select
                                                value={section.category?.id || ""}
                                                onChange={(e) => handleCategorySelect(section.id, e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors[`section_${index}_category`] ? "border-red-500" : "border-gray-300"
                                                    }`}
                                            >
                                                <option value="">No Category (All Categories)</option>
                                                {categories.data.map((category) => (
                                                    <option key={category.id} value={category.id}>
                                                        {category.name}
                                                    </option>
                                                ))}
                                            </select>
                                            {errors[`section_${index}_category`] && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {errors[`section_${index}_category`]}
                                                </p>
                                            )}
                                        </div>

                                        {/* Credits */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Credits *
                                            </label>
                                            <Input
                                                type="number"
                                                value={section.credits}
                                                onChange={(e) => updateSection(section.id, "credits", Number(e.target.value))}
                                                placeholder="0"
                                                className={errors[`section_${index}_credits`] ? "border-red-500" : ""}
                                            />
                                            {errors[`section_${index}_credits`] && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {errors[`section_${index}_credits`]}
                                                </p>
                                            )}
                                        </div>

                                        {/* Amount */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Amount *
                                            </label>
                                            <Input
                                                type="number"
                                                step="0.01"
                                                value={section.amount}
                                                onChange={(e) => updateSection(section.id, "amount", Number(e.target.value))}
                                                placeholder="0.00"
                                                className={errors[`section_${index}_amount`] ? "border-red-500" : ""}
                                            />
                                            {errors[`section_${index}_amount`] && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {errors[`section_${index}_amount`]}
                                                </p>
                                            )}
                                        </div>

                                        {/* Discount */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Discount (%)
                                            </label>
                                            <Input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={section.discount}
                                                onChange={(e) => updateSection(section.id, "discount", Number(e.target.value))}
                                                placeholder="0"
                                                className={errors[`section_${index}_discount`] ? "border-red-500" : ""}
                                            />
                                            {errors[`section_${index}_discount`] && (
                                                <p className="text-red-500 text-sm mt-1">
                                                    {errors[`section_${index}_discount`]}
                                                </p>
                                            )}
                                        </div>

                                        {/* Available Time - Only show for Emergency plans */}
                                        {formData.type === SubscriptionPlanTypeEnum.Emergency && (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Available Time *
                                                </label>
                                                <select
                                                    value={section.availableTime || ""}
                                                    onChange={(e) => updateSection(section.id, "availableTime", e.target.value ? Number(e.target.value) : null)}
                                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors[`section_${index}_availableTime`] ? "border-red-500" : "border-gray-300"}`}
                                                >
                                                    <option value="">Select available time</option>
                                                    <option value={SubscriptionPlanAreaAvailableTimeEnum.Day}>Day (6 AM - 6 PM)</option>
                                                    <option value={SubscriptionPlanAreaAvailableTimeEnum.Night}>Night (6 PM - 6 AM)</option>
                                                    <option value={SubscriptionPlanAreaAvailableTimeEnum.FullDay}>Full Day (24 Hours)</option>
                                                </select>
                                                {errors[`section_${index}_availableTime`] && (
                                                    <p className="text-red-500 text-sm mt-1">
                                                        {errors[`section_${index}_availableTime`]}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Selected Values Display */}
                                    {section.country && (
                                        <div className="mt-4 flex flex-wrap gap-2">
                                            <Badge variant="outline" className="flex items-center gap-1">
                                                <Globe className="h-3 w-3" />
                                                {section.country.name}
                                            </Badge>
                                            {section.category ? (
                                                <Badge variant="outline" className="flex items-center gap-1">
                                                    <Building2 className="h-3 w-3" />
                                                    {section.category.name}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="flex items-center gap-1 bg-gray-100 text-gray-600">
                                                    <Building2 className="h-3 w-3" />
                                                    All Categories
                                                </Badge>
                                            )}
                                            {formData.type === SubscriptionPlanTypeEnum.Emergency && section.availableTime && (
                                                <Badge variant="outline" className="flex items-center gap-1">
                                                    <Clock className="h-3 w-3" />
                                                    {section.availableTime === SubscriptionPlanAreaAvailableTimeEnum.Day && "Day (6 AM - 6 PM)"}
                                                    {section.availableTime === SubscriptionPlanAreaAvailableTimeEnum.Night && "Night (6 PM - 6 AM)"}
                                                    {section.availableTime === SubscriptionPlanAreaAvailableTimeEnum.FullDay && "Full Day (24 Hours)"}
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card>

                    {/* Error Messages */}
                    {(plansError || areasError) && (
                        <Card className="p-4 border-red-200 bg-red-50">
                            <div className="flex items-center gap-2 text-red-700">
                                <AlertCircle className="h-5 w-5" />
                                <div>
                                    <p className="font-medium">Error creating subscription plan</p>
                                    <p className="text-sm">{plansError || areasError}</p>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-3 pt-6 border-t">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                            disabled={plansLoading || areasLoading || deletingAreas}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={plansLoading || areasLoading || deletingAreas}
                            className="flex items-center gap-2"
                        >
                             {(plansLoading || areasLoading || deletingAreas) ? (
                                 <>
                                     <Loader2 className="h-4 w-4 animate-spin" />
                                     {deletingAreas ? "Deleting Areas..." : 
                                      editPlan ? "Updating..." : "Creating..."}
                                 </>
                             ) : (
                                 <>
                                     <Save className="h-4 w-4" />
                                     {editPlan ? "Update Plan" : "Create Plan"}
                                 </>
                             )}
                        </Button>
                    </div>
                </form>
            </motion.div>
        </motion.div>
    );
};

export default SubscriptionPlanModal;
